'use client';

import { useTranslations } from 'next-intl';
import { Icon } from './icon';
import { translateActionError } from '@/lib/utils/translate-action-error';
import styles from './action-error.module.css';

const cx = (...names: Array<string | false | null | undefined>) =>
  names.filter(Boolean).join(' ');

/**
 * Hook variant of `<ActionError>` — returns a translator function suitable for
 * call sites that need the resolved string (e.g. an `<Input error={...} />`
 * prop, or a `setToast(...)` call).
 *
 *   const tErr = useTranslateActionError();
 *   setError(tErr(result.error));
 */
export function useTranslateActionError() {
  const t = useTranslations();
  return (raw: string | null | undefined) => translateActionError(raw, t);
}

interface Props {
  /** Raw error string from a Server Action result. May be a translation key
   *  (prefixed with `errors.`) or free text. */
  raw: string | null | undefined;
  className?: string;
  role?: 'alert' | 'status';
  as?: 'p' | 'div' | 'span';
}

/**
 * Render an action error string, translating it when it's a translation key
 * and passing it through verbatim otherwise. Use this in client components
 * that consume `{ success: false, error: '...' }` results from Server Actions
 * so the migration to translation keys is incremental — old free-text errors
 * still render unchanged.
 *
 * Carries the shared danger-soft card treatment (`.root`) by default — a
 * leading error glyph plus the message text. The caller's `className` is
 * merged after `.root`, so call-site margins/positioning still apply. The
 * error icon is decorative (`aria-hidden`); the `role` on the container is
 * what conveys the message to assistive tech.
 *
 * Internally subscribes to the root next-intl translator, which is required to
 * resolve `errors.*` keys. Don't pass your component's namespaced `t` to
 * `translateActionError` directly — it can only resolve keys under its own
 * namespace.
 */
export function ActionError({
  raw,
  className,
  role = 'alert',
  as: Tag = 'p',
}: Props) {
  const t = useTranslations();
  if (!raw) return null;
  return (
    <Tag className={cx(styles.root, className)} role={role}>
      <span className={styles.icon}>
        <Icon name="error" size={16} />
      </span>
      <span className={styles.text}>{translateActionError(raw, t)}</span>
    </Tag>
  );
}
